"use client";

import { ErrorCode } from "@calcom/features/auth/lib/ErrorCode";
import { HOSTED_CAL_FEATURES } from "@calcom/lib/constants";
import { emailRegex } from "@calcom/lib/emailSchema";
import { getSafeRedirectUrl } from "@calcom/lib/getSafeRedirectUrl";
import { useCompatSearchParams } from "@calcom/lib/hooks/useCompatSearchParams";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { trpc } from "@calcom/trpc/react";
import { Alert } from "@calcom/ui/components/alert";
import { Icon } from "@calcom/ui/components/icon";
import { SAMLLogin } from "@calcom/web/modules/auth/components/SAMLLogin";
import { LastUsed, useLastUsed } from "@calcom/web/modules/auth/hooks/useLastUsed";
import AddToHomescreen from "@components/AddToHomescreen";
import BackupCode from "@components/auth/BackupCode";
import TwoFactor from "@components/auth/TwoFactor";
import { zodResolver } from "@hookform/resolvers/zod";
import type { inferSSRProps } from "@lib/types/inferSSRProps";
import type { getServerSideProps } from "@server/lib/auth/login/getServerSideProps";
import { Calendar, Eye, EyeOff, Shield, Sparkles } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { signIn } from "next-auth/react";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

const AUTO_PRIMARY = "#075F78";
const AUTO_PRIMARY_DARK = "#063D56";
const AUTO_PRIMARY_DEEP = "#042D40";
const AUTO_PRIMARY_SOFT = "rgba(7,95,120,0.10)";
const AUTO_PRIMARY_SHADOW = "rgba(7,95,120,0.26)";

interface LoginValues {
  email: string;
  password: string;
  totpCode: string;
  backupCode: string;
  csrfToken: string;
}

export type PageProps = inferSSRProps<typeof getServerSideProps>;
export default function Login({
  csrfToken,
  isGoogleLoginEnabled,
  isOutlookLoginEnabled,
  isSAMLLoginEnabled,
  samlTenantID,
  samlProductID,
  totpEmail,
}: PageProps) {
  const searchParams = useCompatSearchParams();
  const { t } = useLocale();
  const router = useRouter();
  const formSchema = z
    .object({
      email: z.string().min(1, "Este campo é obrigatório").regex(emailRegex, "Digite um e-mail válido"),
      ...(totpEmail ? {} : { password: z.string().min(1, "Este campo é obrigatório") }),
    })
    .passthrough();
  const methods = useForm<LoginValues>({ resolver: zodResolver(formSchema) });
  const { register, formState } = methods;
  const [twoFactorRequired, setTwoFactorRequired] = useState(!!totpEmail || false);
  const [twoFactorLostAccess, setTwoFactorLostAccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastUsed, setLastUsed] = useLastUsed();
  const [showPassword, setShowPassword] = useState(false);

  const errorMessages: { [key: string]: string } = {
    [ErrorCode.IncorrectEmailPassword]: "E-mail ou senha incorretos",
    [ErrorCode.IncorrectTwoFactorCode]: "Código de verificação incorreto. Tente novamente.",
    [ErrorCode.InternalServerError]: "Algo deu errado. Tente novamente ou entre em contato.",
    [ErrorCode.ThirdPartyIdentityProviderEnabled]: "Conta criada com provedor externo (Google/Microsoft)",
  };

  let callbackUrl = searchParams?.get("callbackUrl") || "";
  if (/"\//.test(callbackUrl)) callbackUrl = callbackUrl.substring(1);

  if (/^https?:\/\//.test(callbackUrl)) {
    const safeCallbackUrl = getSafeRedirectUrl(callbackUrl);
    if (safeCallbackUrl) {
      const safeUrl = new URL(safeCallbackUrl);
      callbackUrl = `${safeUrl.pathname}${safeUrl.search}${safeUrl.hash}` || "/";
    } else {
      callbackUrl = "/";
    }
  } else if (!callbackUrl) {
    callbackUrl = "/dashboard";
  } else if (!callbackUrl.startsWith("/")) {
    callbackUrl = `/${callbackUrl}`;
  }

  const { data, isPending, error } = trpc.viewer.public.ssoConnections.useQuery();

  useEffect(
    function refactorMeWithoutEffect() {
      if (error) {
        setErrorMessage(error.message);
      }
    },
    [error]
  );

  const displaySSOLogin = HOSTED_CAL_FEATURES
    ? true
    : isSAMLLoginEnabled && !isPending && data?.connectionExists;

  const onSubmit = async (values: LoginValues) => {
    setErrorMessage(null);
    const res = await signIn<"credentials">("credentials", {
      ...values,
      callbackUrl,
      redirect: false,
    });
    if (!res) setErrorMessage(errorMessages[ErrorCode.InternalServerError]);
    else if (!res.error) {
      setLastUsed("credentials");
      router.push(callbackUrl);
    } else if (res.error === ErrorCode.SecondFactorRequired) setTwoFactorRequired(true);
    else if (res.error === ErrorCode.IncorrectBackupCode) setErrorMessage("Código de backup incorreto");
    else if (res.error === ErrorCode.MissingBackupCodes) setErrorMessage("Códigos de backup não encontrados");
    else setErrorMessage(errorMessages[res.error] || "Algo deu errado");
  };

  const showSocialLogin = isGoogleLoginEnabled || isOutlookLoginEnabled;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: `linear-gradient(145deg, ${AUTO_PRIMARY_DEEP} 0%, ${AUTO_PRIMARY} 48%, ${AUTO_PRIMARY_DARK} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        position: "relative",
        overflow: "hidden",
      }}>
      {/* ═══ Background decoration ═══ */}
      <div
        style={{
          position: "absolute",
          top: "-160px",
          right: "-140px",
          width: "560px",
          height: "560px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 62%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-260px",
          left: "-140px",
          width: "720px",
          height: "720px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(0,0,0,0.07) 0%, transparent 64%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
          pointerEvents: "none",
          maskImage: "radial-gradient(ellipse 80% 65% at 50% 50%, black 30%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 65% at 50% 50%, black 30%, transparent 100%)",
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          width: "100%",
          maxWidth: "980px",
          borderRadius: "30px",
          overflow: "hidden",
          boxShadow:
            "0 0 0 1px rgba(255,255,255,0.07), 0 20px 80px rgba(4,45,64,0.28), 0 32px 120px rgba(0,0,0,0.16)",
          minHeight: "640px",
        }}>
        <div
          className="hidden lg:flex"
          style={{
            flex: "0 0 42%",
            background: `linear-gradient(180deg, ${AUTO_PRIMARY} 0%, ${AUTO_PRIMARY_DARK} 100%)`,
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderRight: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "46px 40px",
            position: "relative",
          }}>
          <div
            style={{
              position: "absolute",
              top: "16%",
              left: "-18%",
              width: "320px",
              height: "320px",
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              right: "-12%",
              bottom: "-10%",
              width: "240px",
              height: "240px",
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(4,45,64,0.30) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ marginBottom: "44px" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                }}>
                <Image
                  src="/trafega-auto-logo.svg"
                  alt="Trafega Pro"
                  width={54}
                  height={54}
                  style={{ height: "54px", width: "54px", borderRadius: "12px", objectFit: "contain" }}
                />
              </div>
            </div>

            <h1
              style={{
                color: "#ffffff",
                fontSize: "26px",
                fontWeight: 700,
                lineHeight: 1.28,
                marginBottom: "14px",
                letterSpacing: "-0.02em",
                maxWidth: "320px",
              }}>
              Agendamento inteligente para oficinas e autocenters
            </h1>
            <p
              style={{
                color: "rgba(255,255,255,0.72)",
                fontSize: "15px",
                lineHeight: 1.6,
                marginBottom: "34px",
                maxWidth: "310px",
              }}>
              Organize serviços, clientes e atendimentos com uma agenda pensada para o setor automotivo.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <FeaturePill icon={<Calendar size={15} />} text="Agenda de serviços 24h" />
              <FeaturePill icon={<Sparkles size={15} />} text="Atendimento via WhatsApp" />
              <FeaturePill icon={<Shield size={15} />} text="Histórico de cliente e veículo" />
            </div>
          </div>

          <p
            style={{
              marginTop: "auto",
              paddingTop: "40px",
              fontSize: "12px",
              color: "rgba(255,255,255,0.38)",
            }}>
            trafegapro.com.br
          </p>
        </div>

        <div
          style={{
            flex: 1,
            backgroundColor: "#ffffff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "48px 44px",
            position: "relative",
          }}>
          <div className="mb-8 lg:hidden" style={{ textAlign: "center" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "10px" }}>
              <Image
                src="/trafega-auto-logo.svg"
                alt="Trafega Pro"
                width={120}
                height={42}
                style={{ height: "34px", width: "auto", objectFit: "contain" }}
              />
            </div>
          </div>

          <div style={{ marginBottom: "28px" }}>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: 700,
                color: "#111827",
                marginBottom: "8px",
                letterSpacing: "-0.02em",
              }}>
              {twoFactorRequired ? "Verificação em 2 etapas" : "Bem-vindo de volta"}
            </h2>
            <p style={{ fontSize: "15px", color: "#98a2b3" }}>
              {twoFactorRequired
                ? "Digite o código de verificação do seu app"
                : "Faça login para acessar sua operação"}
            </p>
          </div>

          <FormProvider {...methods}>
            {/* Social Login */}
            {!twoFactorRequired && showSocialLogin && (
              <>
                <div style={{ display: "flex", gap: "10px" }}>
                  {isGoogleLoginEnabled && (
                    <SocialButton
                      icon={
                        <Image
                          src="/google-icon-colored.svg"
                          alt=""
                          width={16}
                          height={16}
                          style={{ width: "16px", height: "16px" }}
                        />
                      }
                      label="Google"
                      isLastUsed={lastUsed === "google"}
                      onClick={async () => {
                        setLastUsed("google");
                        await signIn("google", { callbackUrl });
                      }}
                      disabled={formState.isSubmitting}
                    />
                  )}
                  {isOutlookLoginEnabled && (
                    <SocialButton
                      icon={
                        <Image
                          src="/microsoft-logo.svg"
                          alt=""
                          width={16}
                          height={16}
                          style={{ width: "16px", height: "16px" }}
                        />
                      }
                      label="Microsoft"
                      isLastUsed={lastUsed === "microsoft"}
                      onClick={async () => {
                        setLastUsed("microsoft");
                        await signIn("azure-ad", { callbackUrl });
                      }}
                      disabled={formState.isSubmitting}
                    />
                  )}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "16px", margin: "24px 0" }}>
                  <div style={{ flex: 1, height: "1px", backgroundColor: "#eaeef3" }} />
                  <span
                    style={{
                      fontSize: "11px",
                      color: "#b6bfcc",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      fontWeight: 600,
                    }}>
                    ou
                  </span>
                  <div style={{ flex: 1, height: "1px", backgroundColor: "#eaeef3" }} />
                </div>
              </>
            )}

            <form onSubmit={methods.handleSubmit(onSubmit)} noValidate data-testid="login-form">
              <input defaultValue={csrfToken || undefined} type="hidden" hidden {...register("csrfToken")} />

              {!twoFactorRequired && (
                <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
                  <div>
                    <label
                      htmlFor="email"
                      style={{
                        display: "block",
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#344054",
                        marginBottom: "6px",
                      }}>
                      E-mail
                    </label>
                    <input
                      id="email"
                      type="email"
                      defaultValue={totpEmail || (searchParams?.get("email") as string)}
                      autoComplete="email"
                      placeholder="seu@email.com"
                      {...register("email")}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pasted = e.clipboardData.getData("text").trim();
                        methods.setValue("email", pasted, { shouldValidate: true });
                        e.currentTarget.value = pasted;
                      }}
                      onChange={(e) => {
                        const trimmed = e.target.value.trim();
                        if (trimmed !== e.target.value) {
                          e.target.value = trimmed;
                        }
                        register("email").onChange(e);
                      }}
                      style={{
                        width: "100%",
                        padding: "12px 14px",
                        border: "1.5px solid #e6ebf0",
                        borderRadius: "12px",
                        fontSize: "15px",
                        color: "#111827",
                        backgroundColor: "#fbfcfe",
                        outline: "none",
                        transition: "all 0.2s ease",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = AUTO_PRIMARY;
                        e.currentTarget.style.backgroundColor = "#ffffff";
                        e.currentTarget.style.boxShadow = `0 0 0 4px ${AUTO_PRIMARY_SOFT}`;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = "#e6ebf0";
                        e.currentTarget.style.backgroundColor = "#fbfcfe";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    />
                    {formState.errors.email && (
                      <p style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>
                        {formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "6px",
                      }}>
                      <label
                        htmlFor="password"
                        style={{ fontSize: "13px", fontWeight: 600, color: "#344054" }}>
                        Senha
                      </label>
                      <Link
                        href="/auth/forgot-password"
                        style={{
                          fontSize: "12px",
                          color: "#98a2b3",
                          textDecoration: "none",
                          transition: "color 0.15s",
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.color = AUTO_PRIMARY;
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.color = "#9ca3af";
                        }}>
                        Esqueceu a senha?
                      </Link>
                    </div>
                    <div style={{ position: "relative" }}>
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        placeholder="Digite sua senha"
                        {...register("password")}
                        style={{
                          width: "100%",
                          padding: "12px 42px 12px 14px",
                          border: "1.5px solid #e6ebf0",
                          borderRadius: "12px",
                          fontSize: "15px",
                          color: "#111827",
                          backgroundColor: "#fbfcfe",
                          outline: "none",
                          transition: "all 0.2s ease",
                          boxSizing: "border-box",
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = AUTO_PRIMARY;
                          e.currentTarget.style.backgroundColor = "#ffffff";
                          e.currentTarget.style.boxShadow = `0 0 0 4px ${AUTO_PRIMARY_SOFT}`;
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = "#e6ebf0";
                          e.currentTarget.style.backgroundColor = "#fbfcfe";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#c2cad5",
                          padding: "2px",
                          display: "flex",
                        }}>
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {formState.errors.password && (
                      <p style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>
                        {formState.errors.password.message}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {twoFactorRequired && (
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  {!twoFactorLostAccess ? <TwoFactor center /> : <BackupCode center />}
                </div>
              )}

              {errorMessage && <Alert severity="error" title={errorMessage} className="mt-4" />}

              <button
                type="submit"
                disabled={formState.isSubmitting}
                style={{
                  width: "100%",
                  marginTop: "28px",
                  padding: "13px 20px",
                  background: `linear-gradient(90deg, ${AUTO_PRIMARY} 0%, ${AUTO_PRIMARY_DARK} 100%)`,
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "15px",
                  fontWeight: 600,
                  cursor: formState.isSubmitting ? "not-allowed" : "pointer",
                  opacity: formState.isSubmitting ? 0.6 : 1,
                  transition: "all 0.2s ease",
                  boxShadow: `0 10px 24px ${AUTO_PRIMARY_SHADOW}`,
                  letterSpacing: "0.01em",
                }}
                onMouseOver={(e) => {
                  if (!formState.isSubmitting) {
                    e.currentTarget.style.background = `linear-gradient(90deg, ${AUTO_PRIMARY_DARK} 0%, ${AUTO_PRIMARY_DEEP} 100%)`;
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 14px 30px rgba(7,95,120,0.32)";
                  }
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = `linear-gradient(90deg, ${AUTO_PRIMARY} 0%, ${AUTO_PRIMARY_DARK} 100%)`;
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = `0 10px 24px ${AUTO_PRIMARY_SHADOW}`;
                }}>
                {twoFactorRequired ? "Verificar" : "Entrar"}
              </button>
            </form>

            {twoFactorRequired && (
              <div style={{ display: "flex", justifyContent: "center", gap: "16px", marginTop: "16px" }}>
                {!totpEmail ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        if (twoFactorLostAccess) {
                          setTwoFactorLostAccess(false);
                          methods.setValue("backupCode", "");
                        } else {
                          setTwoFactorRequired(false);
                          methods.setValue("totpCode", "");
                        }
                        setErrorMessage(null);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        background: "none",
                        border: "none",
                        fontSize: "13px",
                        color: "#6b7280",
                        cursor: "pointer",
                      }}>
                      <Icon name="arrow-left" className="size-4" />
                      Voltar
                    </button>
                    {!twoFactorLostAccess && (
                      <button
                        type="button"
                        onClick={() => {
                          setTwoFactorLostAccess(true);
                          setErrorMessage(null);
                          methods.setValue("totpCode", "");
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          background: "none",
                          border: "none",
                          fontSize: "13px",
                          color: "#6b7280",
                          cursor: "pointer",
                        }}>
                        <Icon name="lock" className="size-4" />
                        Perdi o acesso
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      window.location.replace("/");
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "13px",
                      color: "#6b7280",
                      cursor: "pointer",
                    }}>
                    Cancelar
                  </button>
                )}
              </div>
            )}
          </FormProvider>

          {!twoFactorRequired && displaySSOLogin && (
            <div style={{ display: "flex", justifyContent: "center", marginTop: "24px" }}>
              <SAMLLogin
                samlTenantID={samlTenantID}
                samlProductID={samlProductID}
                setErrorMessage={setErrorMessage}
                color="minimal"
                StartIcon={undefined}
                className="text-sm font-medium hover:underline p-0 h-auto"
              />
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "14px",
              marginTop: "28px",
              fontSize: "12px",
            }}>
            <Link href="/privacy" style={{ color: "#7b8794", textDecoration: "none" }}>
              Política de Privacidade
            </Link>
            <span style={{ color: "#d1d7df" }} aria-hidden="true">
              •
            </span>
            <Link href="/sobre" style={{ color: "#7b8794", textDecoration: "none" }}>
              Sobre o Trafega Pro
            </Link>
            <span style={{ color: "#d1d7df" }} aria-hidden="true">
              •
            </span>
            <Link href="/terms" style={{ color: "#7b8794", textDecoration: "none" }}>
              Termos de Serviço
            </Link>
          </div>
        </div>
      </div>

      <AddToHomescreen />
    </div>
  );
}

/* ═══ Feature pill for left panel ═══ */
function FeaturePill({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "12px 14px",
        borderRadius: "12px",
        backgroundColor: "rgba(255,255,255,0.07)",
        border: "1px solid rgba(255,255,255,0.10)",
        transition: "all 0.2s ease",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.11)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.16)";
        e.currentTarget.style.transform = "translateX(4px)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.07)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)";
        e.currentTarget.style.transform = "translateX(0)";
      }}>
      <div
        style={{
          flexShrink: 0,
          width: "30px",
          height: "30px",
          borderRadius: "8px",
          backgroundColor: "rgba(255,255,255,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "rgba(255,255,255,0.88)",
        }}>
        {icon}
      </div>
      <span style={{ color: "rgba(255,255,255,0.88)", fontSize: "14px", fontWeight: 600 }}>{text}</span>
    </div>
  );
}

/* ═══ Social login button ═══ */
function SocialButton({
  icon,
  label,
  isLastUsed,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  isLastUsed: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        padding: "11px 16px",
        border: "1.5px solid #e8edf3",
        borderRadius: "12px",
        backgroundColor: "#fbfcfe",
        fontSize: "14px",
        fontWeight: 600,
        color: "#374151",
        cursor: "pointer",
        transition: "all 0.15s ease",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = "#ffffff";
        e.currentTarget.style.borderColor = "#d8e2eb";
        e.currentTarget.style.boxShadow = "0 6px 18px rgba(15,23,42,0.05)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = "#fbfcfe";
        e.currentTarget.style.borderColor = "#e8edf3";
        e.currentTarget.style.boxShadow = "none";
      }}>
      {icon}
      <span>{label}</span>
      {isLastUsed && <LastUsed />}
    </button>
  );
}
